import type { Channel, ContentFormat, PillarSlug } from "@/api/client";
import { PILLARS } from "@/lib/pmbok";

/**
 * Natural-language quick-add for content. Single pass, clause-stripping with
 * allow-lists. Every clause is only consumed if it *resolves* — an unknown
 * word stays in the title rather than being silently eaten.
 *
 *   "draft a thread about risk on linkedin tomorrow"
 *     → { title: "a thread"  ... } no — see below; verb + thread + about risk
 *        + on linkedin + tomorrow are stripped, title = "" → caller falls back
 *
 * Clause order: verb → channel (@x / on x) → pillar (#x / about x)
 *               → format word → date phrase → remaining text = title.
 */

export interface ParsedContent {
  title: string;
  channelId: number | null;
  channelSlug: string | null;
  channelName: string | null;
  pillar: PillarSlug | null;
  format: ContentFormat;
  scheduledFor: number | null;
}

const VERB_RE =
  /^(?:add|draft|write|create|make|plan|schedule|post|publish|film|record|shoot|design)\s+/i;

// Format words → canonical slug. Order matters: multi-word / specific first.
const FORMAT_WORDS: { re: RegExp; slug: ContentFormat }[] = [
  { re: /\bnewsletters?\b/i, slug: "newsletter" },
  { re: /\b(?:blog\s+posts?|blogs?|articles?)\b/i, slug: "article" },
  { re: /\bcarousels?\b/i, slug: "carousel" },
  { re: /\bthreads?\b/i, slug: "thread" },
  { re: /\b(?:livestreams?|lives)\b/i, slug: "livestream" },
  { re: /\b(?:shorts?|reels?)\b/i, slug: "short" },
  { re: /\bvideos?\b/i, slug: "video" },
  { re: /\bposts?\b/i, slug: "post" },
];

// Short aliases people type for channels, mapped to canonical slugs.
const CHANNEL_ALIASES: Record<string, string> = {
  li: "linkedin",
  ig: "instagram",
  insta: "instagram",
  yt: "youtube",
  twitter: "x",
  fb: "facebook",
};

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const MONTHS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}
function startOfToday(now: number): Date {
  const d = new Date(now * 1000);
  d.setHours(0, 0, 0, 0);
  return d;
}
function toSec(d: Date): number {
  return Math.floor(d.getTime() / 1000);
}

function resolveChannel(tokenRaw: string, channels: Channel[]): Channel | null {
  const token = tokenRaw.toLowerCase().replace(/^@/, "");
  const slug = CHANNEL_ALIASES[token] ?? token;
  // slug exact
  let hit = channels.find((c) => c.slug.toLowerCase() === slug);
  if (hit) return hit;
  // name exact
  hit = channels.find((c) => c.name.toLowerCase() === token);
  if (hit) return hit;
  // name starts-with (>= 2 chars to avoid noise)
  if (token.length >= 2) {
    hit = channels.find((c) => c.name.toLowerCase().startsWith(token));
    if (hit) return hit;
  }
  return null;
}

function resolvePillar(tokenRaw: string): PillarSlug | null {
  const token = tokenRaw.toLowerCase().replace(/^#/, "");
  for (const p of PILLARS) {
    const label = p.label.toLowerCase();
    if (p.slug === token || label === token || label.startsWith(token)) {
      return p.slug;
    }
  }
  return null;
}

/** Find a date phrase, return its unix-seconds value and the matched substring. */
function parseWhen(text: string, now: number): { when: number; match: string } | null {
  const today = startOfToday(now);

  // today / tomorrow / next week
  let m = text.match(/\b(today)\b/i);
  if (m) return { when: toSec(today), match: m[0] };

  m = text.match(/\b(tomorrow)\b/i);
  if (m) {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return { when: toSec(d), match: m[0] };
  }

  m = text.match(/\bnext\s+week\b/i);
  if (m) {
    const d = new Date(today);
    d.setDate(d.getDate() + 7);
    return { when: toSec(d), match: m[0] };
  }

  // in N days / in N weeks
  m = text.match(/\bin\s+(\d{1,3})\s+(days?|weeks?)\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    const mult = /week/i.test(m[2]) ? 7 : 1;
    const d = new Date(today);
    d.setDate(d.getDate() + n * mult);
    return { when: toSec(d), match: m[0] };
  }

  // [next] [on] <weekday>
  m = text.match(/\b(next\s+|on\s+)?(sun|mon|tue|wed|thu|fri|sat)[a-z]*\b/i);
  if (m) {
    const idx = WEEKDAYS.findIndex((w) => w.startsWith(m![2].toLowerCase()));
    if (idx !== -1) {
      const dow = today.getDay();
      let ahead = (idx - dow + 7) % 7;
      if (ahead === 0) ahead = 7; // "monday" on a Monday → next week
      const d = new Date(today);
      d.setDate(d.getDate() + ahead);
      return { when: toSec(d), match: m[0] };
    }
  }

  // [on] <Month> <day> (optionally with year)
  m = text.match(
    /\b(?:on\s+)?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?\b/i,
  );
  if (m) {
    const mi = MONTHS.findIndex((mo) => mo.startsWith(m![1].toLowerCase()));
    const day = parseInt(m[2], 10);
    if (mi !== -1 && day >= 1 && day <= 31) {
      const year = m[3] ? parseInt(m[3], 10) : today.getFullYear();
      const d = new Date(year, mi, day);
      d.setHours(0, 0, 0, 0);
      // If no explicit year and the date already passed, roll to next year.
      if (!m[3] && toSec(d) < toSec(today)) d.setFullYear(year + 1);
      return { when: toSec(d), match: m[0] };
    }
  }

  return null;
}

function tidy(s: string): string {
  return s
    .replace(/\s+/g, " ")
    .replace(/\s*[·:–—-]\s*$/g, "")
    .replace(/^\s*[·:–—-]\s*/g, "")
    .trim();
}

export function parseContent(
  input: string,
  channels: Channel[],
  now: number = nowSec(),
): ParsedContent {
  let rest = input.trim();

  let channelId: number | null = null;
  let channelSlug: string | null = null;
  let channelName: string | null = null;
  let pillar: PillarSlug | null = null;
  let format: ContentFormat = "post";
  let scheduledFor: number | null = null;

  // 1) leading verb
  rest = rest.replace(VERB_RE, "");

  // 2) channel — explicit @handle first, then "on <word>"
  const at = rest.match(/(^|\s)@([a-z0-9_.-]+)/i);
  if (at) {
    const ch = resolveChannel(at[2], channels);
    if (ch) {
      channelId = ch.id;
      channelSlug = ch.slug;
      channelName = ch.name;
      rest = rest.replace(at[0], " ");
    }
  }
  if (channelId == null) {
    const on = rest.match(/\bon\s+([a-z][a-z0-9_]*)\b/i);
    if (on) {
      const ch = resolveChannel(on[1], channels);
      if (ch) {
        channelId = ch.id;
        channelSlug = ch.slug;
        channelName = ch.name;
        rest = rest.replace(on[0], " ");
      }
    }
  }

  // 3) pillar — explicit #tag first, then "about <word>"
  const hash = rest.match(/(^|\s)#([a-z0-9_-]+)/i);
  if (hash) {
    const p = resolvePillar(hash[2]);
    if (p) {
      pillar = p;
      rest = rest.replace(hash[0], " ");
    }
  }
  if (pillar == null) {
    const about = rest.match(/\babout\s+([a-z][a-z0-9_]*)\b/i);
    if (about) {
      const p = resolvePillar(about[1]);
      if (p) {
        pillar = p;
        rest = rest.replace(about[0], " ");
      }
    }
  }

  // 4) format word
  for (const f of FORMAT_WORDS) {
    const m = rest.match(f.re);
    if (m) {
      format = f.slug;
      rest = rest.replace(m[0], " ");
      break;
    }
  }

  // 5) date phrase
  const when = parseWhen(rest, now);
  if (when) {
    scheduledFor = when.when;
    rest = rest.replace(when.match, " ");
  }

  // 6) leftover = title
  const title = tidy(rest);

  return { title, channelId, channelSlug, channelName, pillar, format, scheduledFor };
}
